import { useQuery, useMutation } from '@tanstack/react-query';
import { generateHookExports } from 'api/generateExports';
import { createCRUDService } from 'api/queryService';
import { API_CONFIG } from 'services/apiConfig';
import axios from "../../axios";
import { formatPayloadIfNeeded } from 'api/utils';
import { showToast } from 'services/utils';

let subQueryData = API_CONFIG.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTOR.parts?.find((item) => item?.queryKey === "order");
let baseData = `${API_CONFIG.CUSTOMER_RELATED_PROCESS_AND_DISTRIBUTOR?.path}/${subQueryData?.url}`;

const BASE_URL = baseData;
const QUERY_KEY = subQueryData?.queryKey;

const customerOrderService = createCRUDService({
    baseUrl: BASE_URL,
    queryKey: QUERY_KEY,
    custom: {
        getByLink: (params = {}, options = {}) => {
            return useQuery({
                queryKey: [QUERY_KEY, params],
                queryFn: () => axios.get(`${BASE_URL}/get-by-link`, { params }).then((res) => res.data),
                ...options,
                retry: options.retry ?? 1,
                onError: (error) => {
                    showToast(error?.data);
                },
            })
        },
        getStock: (id, options = {}) => {
            return useQuery({
                queryKey: [QUERY_KEY, id],
                queryFn: () => axios.get(`${BASE_URL}/get-stock/${id}`).then((res) => res.data),
                ...options,
                retry: options.retry ?? 1,
                onError: (error) => {
                    showToast(error?.data);
                },
            })
        },
        createLinkingDocument: () => {
            return useMutation({
                mutationFn: (payload) => {
                    const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
                    return axios
                        .post(`${BASE_URL}/create-linking-document`, data, {
                            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
                        })
                        .then((res) => res.data);
                },
                onError: (error) => {
                    showToast(error?.data);
                },
            });
        },
        uploadTrainingDocument: () => {
            return useMutation({
                mutationFn: ({ id, data: payload }) => {
                    const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
                    return axios
                        .put(`${BASE_URL}/upload-user-training-document/${id}`, data, {
                            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
                        })
                        .then((res) => res.data);
                },
                onSuccess: (data) => {
                    showToast(data);
                },
                onError: (error) => {
                    showToast(error?.data);
                },
            });
        },
        getUserSpecific: (params = {}, options = {}) => {
            return useQuery({
                queryKey: [QUERY_KEY, params],
                queryFn: () => axios.get(`${BASE_URL}/get-user-specific`, { params }).then((res) => res.data),
                ...options,
                retry: options.retry ?? 1,
                onError: (error) => {
                    showToast(error?.data);
                },
            })
        },
        getAllPendingOrders: (params = {}, options = {}) => {
            return useQuery({
                queryKey: [QUERY_KEY, params],
                queryFn: () => axios.get(`${BASE_URL}/get-all-pending`, { params }).then((res) => res.data),
                ...options,
                retry: options.retry ?? 1,
                onError: (error) => {
                    showToast(error?.data);
                },
            })
        },
    }
});

export const {
    customerOrderList,
    customerOrderCreate,
    customerOrderGetById,
    customerOrderDownload,
    customerOrderUpdate,
    customerOrderUpdateSpecific,
    customerOrderDelete,
    customerOrderGetByLink,
    customerOrderGetStock,
    customerOrderCreateLinkingDocument,
    customerOrderUploadTrainingDocument,
    customerOrderGetUserSpecific,
    customerOrderGetAllPendingOrders
} = generateHookExports(customerOrderService, 'customerOrder');
